import type { BpmnElement, Shape } from "bpmn-js/lib/model/Types";
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
import type { OverlayAttrs } from "diagram-js/lib/features/overlays/Overlays";
import Button from "primevue/button";
import { h, onMounted, onUnmounted, ref, render, watch } from "vue";
import type { Ref, VNode } from "vue";

import ProcessElement from "@/components/BPMN/ProcessElement.vue";
import RoleSelector from "@/components/BPMN/RoleSelector.vue";
import { vnodeToHtml } from "@/utils";

import { useEditManager } from "../edit-manager";
import {
    HIGH_PRIORITY,
    canHaveContent,
    elementIsLane,
    elementIsSubProcessShape,
    elementIsTask,
    elementType,
} from "./helpers";
import type { useBpmnManager } from "./index";

interface LayerDefinition {
    label: string;
    type: string;
    placement?: Placement;
    /**
     * The VNode is ONLY created ont he first pass and never recreated.
     * If the VNode must be reactive to any properties, make sure the properties
     * itself are reactive. E.g. by providing a `Ref` or fetcher instead of the
     * value directly.
     */
    vnode({ element }: { element: BpmnElement }): VNode | undefined;
    forceRewrite?(): boolean;
    css?({ element }: { element: BpmnElement }): string;
    condition?({
        element,
        selected,
    }: {
        element: BpmnElement;
        selected: boolean;
    }): boolean;
    onStateChange?: ({
        hover,
        select,
        modifying,
        elementId,
        overlayId,
    }: {
        elementId: string;
        overlayId: string;
        hover?: boolean;
        select?: boolean;
        modifying?: boolean;
    }) => void;
    initialState?: { hover?: boolean; select?: boolean; modifying?: boolean };
}

type Position = OverlayAttrs["position"];
type Placement =
    | "TopLeft"
    | "AboveTopMiddle"
    | "TopRight"
    | "BottomLeft"
    | "BottomRight"
    | "Center";

const layerVisibility: Ref<Record<string, boolean>> = ref({
    subprocesses: true,
    files: true,
    dmn: true,
    form: true,
    missingTaskNanoid: true,
    navigationalElement: true,
    taskWindowElement: false,
    roleSelect: true,
    callActivityMapper: true,
    collapsedPoolMapper: true,
    highlight: true,
});

interface NavigationalElementCallbacks {
    onHoveringNavigationalElement: (elementId: string | null) => void;
}

interface Features {
    dotForMissingTasks: {
        enabled: Readonly<Ref<boolean>>;
    };
    navigationalElements: {
        enabled: Readonly<Ref<boolean>>;
        callbacks?: NavigationalElementCallbacks;
    };
    taskWindowElements: {
        enabled: Readonly<Ref<boolean>>;
    };
    roleSelect: {
        enabled: Readonly<Ref<boolean>>;
    };
    highlight: {
        enabled: Readonly<Ref<"onHover" | "onExternal" | "onAll" | false>>;
        highlightSeletor?: (element: BpmnElement) => boolean;
    };
    hoveringPopup: {
        enabled: Readonly<Ref<boolean>>;
    };
}

export function useBpmnLayerManager(
    bpmnManager: ReturnType<typeof useBpmnManager>,
    features?: Features
) {
    const isMounted = ref(false);
    const { isEditing } = useEditManager();

    const definitions = [
        {
            label: "SubProcesses",
            type: "subprocesses",
            placement: "BottomRight",
            vnode: ({ element }) =>
                h(Button, {
                    onClick: () => {
                        bpmnManager.selectSubProcess(element);
                    },
                    icon: "pi pi-window-maximize",
                    severity: "secondary",
                    rounded: true,
                    size: "small",
                }),
            condition: ({ element }) => {
                return elementIsSubProcessShape(element);
            },
        },
    ] as LayerDefinition[];

    function getLayerDefinitions() {
        const layerDefinitions = [...definitions];

        if (!features) {
            return layerDefinitions;
        }

        if (features.dotForMissingTasks?.enabled?.value) {
            layerDefinitions.push({
                label: "TaskWithoutNanoID",
                type: "missingTaskNanoid",
                placement: "BottomLeft",
                vnode: () =>
                    h("i", {
                        class: "pi pi-circle-fill element-without-nanoid",
                    }),
                condition: ({ element }) =>
                    canHaveContent(element) && !element.processElementId,
            });
        }

        if (features.navigationalElements?.enabled?.value) {
            layerDefinitions.push({
                label: "NavigationalElement",
                type: "navigationalElement",
                placement: "Center",
                vnode: ({ element }) => {
                    return h(ProcessElement, {
                        element,
                        elementType: elementType(element),
                        bpmnManager,
                        isHoverPopupEnabled:
                            features.hoveringPopup?.enabled.value,
                    });
                },
                forceRewrite: () => true,
                condition: ({ element }) => {
                    const bo = getBusinessObject(element);

                    // Skip expanded pools as they are just containers and would
                    // block interaction with elements inside them.
                    if (is(element, "bpmn:Participant") && !element.collapsed) {
                        return false;
                    }

                    return (
                        bo.processElementId ||
                        bo.processId ||
                        bo.calledElementId
                    );
                },
                css: ({ element }) => {
                    // Note, so far, below only works for task-like elements. This should be extended once we support
                    // more types of navigational elements. Using :hover is much faster, [data-hover] is for when
                    // hover is triggered through the highlight function.
                    const gElement = `g[data-element-id="${element.id}"]`;

                    if (isEditing.value) {
                        return "";
                    }

                    return `
                        ${gElement} * {
                            cursor: pointer !important;
                        }
                    `;
                },
                onStateChange: ({ elementId, hover }) => {
                    if (!bpmnManager.elementRegistry) return;
                    if (hover === true) {
                        features.navigationalElements.callbacks?.onHoveringNavigationalElement(
                            elementId
                        );
                    } else if (hover === false) {
                        features.navigationalElements.callbacks?.onHoveringNavigationalElement(
                            null
                        );
                    }
                },
            });
        }

        if (features.highlight?.enabled?.value) {
            layerDefinitions.push({
                label: "Highlight",
                type: "highlight",
                placement: "TopLeft",
                vnode: () => h("div", { class: "highlight" }),
                css: ({ element }) => {
                    const gElement = `g[data-element-id="${element.id}"]`;
                    const externalHighlight = `
                        /* Element. */
                        ${gElement}[data-hover] g.djs-visual circle,
                        ${gElement}[data-hover] g.djs-visual polygon,
                        ${gElement}[data-hover] g.djs-visual rect {
                            fill: var(--stekz-primary-color) !important;
                        }

                        /* Text inside elements. */
                        ${gElement}[data-hover] g.djs-visual text {
                            fill: white !important;
                        }

                        /* Paths inside gateways and event (e.g. envelope for message event). */
                        ${gElement}[data-hover] g.djs-visual polygon + path,
                        ${gElement}[data-hover] g.djs-visual circle + path,
                        ${gElement}[data-hover] g.djs-visual rect + path {
                            fill: var(--stekz-primary-color) !important;
                            stroke: white !important;
                        }
                    `;

                    const hoverHighlight = `
                        /* Element. */
                        ${gElement}:hover g.djs-visual rect,
                        ${gElement}:hover g.djs-visual circle,
                        ${gElement}:hover g.djs-visual polygon {
                            fill: var(--stekz-primary-color) !important;
                        }

                        /* Text inside elements. */
                        ${gElement}:hover g.djs-visual text {
                            fill: white !important;
                        }

                        /* Paths inside gateways and event (e.g. envelope for message event). */
                        ${gElement}:hover g.djs-visual polygon + path,
                        ${gElement}:hover g.djs-visual circle + path,
                        ${gElement}:hover g.djs-visual rect + path {
                            fill: var(--stekz-primary-color) !important;
                            stroke: white !important;
                        }
                    `;

                    if (features.highlight.enabled.value === "onExternal") {
                        return externalHighlight;
                    } else {
                        return `
                            ${externalHighlight}
                            ${hoverHighlight}
                        `;
                    }
                },
                condition: ({ element }) =>
                    features.highlight.highlightSeletor
                        ? features.highlight.highlightSeletor(element)
                        : !is(element, "bpmn:Collaboration"),
                onStateChange: ({ elementId, hover, select }) => {
                    if (!bpmnManager.elementRegistry) return;
                    const graphics =
                        bpmnManager.elementRegistry.getGraphics(elementId);
                    if (!graphics) return;

                    if (
                        features.highlight.enabled.value === "onHover" ||
                        features.highlight.enabled.value === "onAll"
                    ) {
                        if (hover === true) {
                            graphics.dataset.hover = "";
                        } else if (hover === false) {
                            delete graphics.dataset.hover;
                        }
                    }

                    if (
                        features.highlight.enabled.value === "onExternal" ||
                        features.highlight.enabled.value === "onAll"
                    ) {
                        if (select === true) {
                            graphics.dataset.hover = "";
                        } else if (select === false) {
                            delete graphics.dataset.hover;
                        }
                    }
                },
            });
        }

        if (features.taskWindowElements?.enabled?.value) {
            const icon = h("i", {
                class: "pi pi-folder-plus inline-task-page",
            });

            layerDefinitions.push({
                label: "TaskWindowElement",
                type: "taskWindowElement",
                placement: "AboveTopMiddle",
                vnode: () => icon,
                condition: ({ element, selected }) =>
                    canHaveContent(element) && selected,
            });
        }

        if (features.roleSelect?.enabled?.value) {
            layerDefinitions.push({
                label: "roleSelect",
                type: "roleSelect",
                placement: "TopLeft",
                vnode: ({ element }) => {
                    return h(RoleSelector, { element, bpmnManager });
                },
                condition: ({ element }) => elementIsLane(element),
            });
        }
        return layerDefinitions;
    }

    const getHtmlId = (element: BpmnElement, definition: LayerDefinition) => {
        return `${element.businessObject.id}_${definition.type}`;
    };
    const htmls: Record<string, HTMLElement> = {};
    /**
     * Get HTML from overlayDefinition for given element and state
     */
    const getHtml = (
        element: BpmnElement,
        definition: LayerDefinition
    ): HTMLElement | undefined => {
        const id = getHtmlId(element, definition);

        const vnode = definition.vnode({ element });

        if (!vnode) return;

        // Generate HTML if not yet exists.
        if (!(id in htmls) || definition.forceRewrite?.()) {
            htmls[id] = vnodeToHtml(vnode);
        }
        return htmls[id];
    };

    /**
     * Set visibility for overlay.
     */
    const setLayerVisibility = (type: string, visible: boolean) => {
        layerVisibility.value[type] = visible;
        const overlays = bpmnManager.bpmnJs.value!.get("overlays");
        const _overlays = overlays!.get({ type: type });
        const elmOverlays =
            _overlays instanceof Array ? _overlays : [_overlays];

        elmOverlays.forEach((overlay) => {
            overlay.htmlContainer.style.display = visible ? "" : "none";
        });
    };

    /**
     * Get the width and height for a given element, scale factored in.
     */
    const getElementSize = (
        htmlElement: HTMLElement
    ): { width: number; height: number } => {
        const viewbox = bpmnManager.canvas?.viewbox();
        const { width, height } = htmlElement.getBoundingClientRect();
        if (viewbox?.scale) {
            return {
                width: width / viewbox.scale,
                height: height / viewbox.scale,
            };
        } else {
            return { width, height };
        }
    };

    /**
     * Some elements have thick lines which we do not want to have overlap
     * with when showing an icon. To account for this, we return an offset which
     * can be used to position the overlay.
     */
    const getElementOffset = (
        element: BpmnElement
    ): { widthOffset: number; heightOffset: number } => {
        let widthOffset = 0;
        let heightOffset = 0;

        if (elementIsTask(element)) {
            widthOffset = 1;
            heightOffset = 1;
        }

        const viewbox = bpmnManager.canvas?.viewbox();
        if (viewbox?.scale) {
            return {
                widthOffset: widthOffset / viewbox.scale,
                heightOffset: heightOffset / viewbox.scale,
            };
        } else {
            return { widthOffset, heightOffset };
        }
    };

    /**
     * Get the initial position for an element in the layer.
     * The element MUST be drawn already before calling this method.
     */
    const getInitialPosition = (
        element: BpmnElement,
        htmlElement: HTMLElement,
        placement: Placement = "BottomLeft"
    ): Position => {
        const viewbox = bpmnManager.canvas?.viewbox();
        const { width: widthWithoutOffset, height: heightWithoutOffset } =
            getElementSize(htmlElement);
        const { widthOffset, heightOffset } = getElementOffset(element);
        const width = widthWithoutOffset + widthOffset;
        const height = heightWithoutOffset + heightOffset;
        const bounds = element.di?.bounds;

        if (element.type.toLowerCase().includes("event")) {
            return { left: 0, bottom: -height };
        } else if (element.type.toLowerCase().includes("flow")) {
            if (viewbox) {
                return { left: 10 / viewbox.scale, bottom: 25 / viewbox.scale };
            } else {
                return { left: 10, bottom: 25 };
            }
        } else {
            if (placement === "BottomLeft") {
                return { left: 0, bottom: height };
            } else if (placement === "TopLeft") {
                return { left: 0, top: 0 };
            } else if (placement === "AboveTopMiddle") {
                if (bounds) {
                    return {
                        left: bounds.width / 2 - width / 2,
                        top: -height - 12,
                    };
                }
                return { left: width / 2, top: 0 };
            } else if (placement === "TopRight") {
                return { right: width, top: 0 };
            } else if (placement === "BottomRight") {
                return { right: width, bottom: height };
            } else if (placement === "Center") {
                if (bounds) {
                    return {
                        left: bounds.width / 2 - width / 2,
                        top: bounds.height / 2 - height / 2,
                    };
                }
                return { left: 0, top: 0 };
            }
        }
        throw Error("Doesn't support given combination of parameters.");
    };

    /**
     * Get the position for the next element in the layer.
     */
    const getNextPosition = (
        lastPosition: Position,
        currentElement: HTMLElement,
        direction: "right" = "right"
    ): Position => {
        const { width } = getElementSize(currentElement);
        if (direction !== "right") {
            throw Error("Currently only supports 'right' as direction.");
        }
        return { ...lastPosition, left: (lastPosition.left || 0) + width };
    };

    interface StateOverlays {
        [elementId: string]: {
            [overlayId: string]: LayerDefinition;
        };
    }
    const overlaysListeningToState: Ref<StateOverlays> = ref({});

    /**
     * Add all overlays as defined in layerDefinitions
     */
    const update = () => {
        // Check if the component is still mounted.
        if (!isMounted.value) return;
        const overlays = bpmnManager.bpmnJs.value?.get("overlays");
        if (!overlays) return;

        let customCss = "";
        bpmnManager.elementRegistry!.forEach((element) => {
            let position: Position | undefined;
            const bpmnElement = element as BpmnElement;

            const layerDefinitions = getLayerDefinitions();
            const expectedOverlays = layerDefinitions
                .filter((layer) => {
                    const result =
                        layer.condition?.({
                            element: bpmnElement,
                            selected: Boolean(
                                bpmnManager.selection
                                    ?.get()
                                    .includes(bpmnElement)
                            ),
                        }) && !element.type.toLowerCase().includes("label");
                    if (!result) {
                        const id = getHtmlId(bpmnElement, layer);
                        delete htmls[id];
                    }
                    return result;
                })
                .filter(({ type }) => {
                    return layerVisibility.value[type];
                });

            const currentOverlays = layerDefinitions.flatMap(
                ({ type }) => overlays.get({ element: bpmnElement, type }) || []
            );

            const overlaysToBeRemoved = currentOverlays.filter(
                (overlay) =>
                    !expectedOverlays.find(
                        (definition) =>
                            definition.type === overlay.type &&
                            overlay.element === element
                    )
            );

            expectedOverlays.forEach((definition) => {
                const htmlElement = getHtml(bpmnElement, definition);
                if (!htmlElement) {
                    return;
                }

                let overlayId = currentOverlays.find(
                    (overlay) => overlay.type === definition.type
                )?.id;

                // Add the overlay if it doesn't exist. We'll update the position later.
                if (!overlayId) {
                    overlayId = overlays.add(bpmnElement, definition.type, {
                        position: {},
                        html: htmlElement,
                    });
                }

                position =
                    position ??
                    getInitialPosition(
                        bpmnElement,
                        htmlElement,
                        definition.placement
                    );

                // Note, this hidden function updates the position
                // of the overlay without having to recreate it. This way
                // we can re-use overlay ids and keep things fast.
                // @ts-expect-error 2339
                overlays._updateOverlay({
                    ...overlays.get(overlayId),
                    position,
                });

                if (definition.onStateChange) {
                    if (!overlaysListeningToState.value[element.id]) {
                        overlaysListeningToState.value[element.id] = {};
                    }

                    overlaysListeningToState.value[element.id][overlayId] =
                        definition;

                    // Trigger any given "initialState".
                    if (definition.initialState) {
                        definition.onStateChange({
                            ...definition.initialState,
                            elementId: element.id,
                            overlayId: overlayId,
                        });
                    }
                }

                if (definition.css) {
                    customCss += definition
                        .css({ element: bpmnElement })
                        .trim();
                }

                position = getNextPosition(position, htmlElement);
            });

            overlaysToBeRemoved.forEach((overlay) => {
                delete overlaysListeningToState.value[element.id];
                overlays.remove({ id: overlay.id });
            });
        });

        bpmnManager.injectStyles(customCss);
    };

    // Using debounce, because bpmnManager.elementRegistry.forEach internally
    // uses 'var' to point to a specific element in a map, which is an issue
    // when update is called multiple times in the same timespan, because
    // 'var' might be updated while the forEach loop is still running for the
    // 'other' update. Something something hoisting.
    const DEBOUNCE_MS = 10;

    /**
     * Add all overlays as defined in layerDefinitions
     */
    const attach = async () => {
        if (!bpmnManager.initialized) {
            console.warn(
                "You've attached this too early. First initialize a modeler or viewer."
            );
        }
        update();
        bpmnManager.bpmnJs.value!.on("import.done", () => {
            update();
            setTimeout(() => {
                // Execute any state changes for the default state.
                Object.entries(overlaysListeningToState.value).forEach(
                    ([elementId, overlays]) => {
                        Object.entries(overlays).forEach(
                            ([overlayId, definition]) => {
                                definition.onStateChange?.({
                                    elementId: elementId,
                                    modifying: false,
                                    hover: false,
                                    overlayId,
                                });
                            }
                        );
                    }
                );
            }, DEBOUNCE_MS);
        });

        /**
         * Properly unmount Vue components from overlays before they are removed from the DOM.
         */
        bpmnManager.eventBus!.on<{ context: { shape: Shape } }>(
            "commandStack.shape.delete.preExecute",
            HIGH_PRIORITY,
            ({ context }) => {
                const overlays = bpmnManager.bpmnJs.value?.get("overlays");
                let overlay = overlays!.get({ element: context.shape.id });
                if (Symbol.iterator in overlay) {
                    overlay = overlay[0];
                }
                if (!overlay) return;
                // Properly trigger unmounting of the Vue elements (if any);
                try {
                    render(null, overlay.html as HTMLElement);
                } catch (e) {
                    console.debug("Could not unmount, due to: " + e);
                }
            }
        );

        bpmnManager.eventBus!.on("element.click", () => {
            update();
            // Set ALL overlays to modifying false.
            Object.entries(overlaysListeningToState.value).forEach(
                ([elementId, overlays]) => {
                    Object.entries(overlays).forEach(
                        ([overlayId, definition]) => {
                            definition.onStateChange?.({
                                elementId: elementId,
                                modifying: false,
                                overlayId,
                            });
                        }
                    );
                }
            );
        });

        bpmnManager.eventBus!.on(
            "element.dblclick",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to modifying true.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            modifying: true,
                            overlayId,
                        });
                    });
                }
            }
        );

        bpmnManager.eventBus!.on(
            "element.hover",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to hover true.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            hover: true,
                            overlayId,
                        });
                    });
                }
            }
        );
        bpmnManager.eventBus!.on(
            "element.out",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to hover false.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            hover: false,
                            overlayId,
                        });
                    });
                }
            }
        );

        bpmnManager.eventBus!.on("canvas.destroy", () => {
            for (const id in htmls) {
                render(null, htmls[id]);
                delete htmls[id];
            }
        });
    };

    watch(
        [
            bpmnManager.initialized,
            () => features?.roleSelect?.enabled.value,
            () => features?.navigationalElements?.enabled.value,
            () => features?.dotForMissingTasks?.enabled.value,
        ],
        () => {
            if (!bpmnManager.initialized.value) {
                return;
            }
            update();
        }
    );

    const highlight = (elementId: string) => {
        if (elementId in overlaysListeningToState.value) {
            Object.entries(overlaysListeningToState.value[elementId]).forEach(
                ([overlayId, definition]) => {
                    definition.onStateChange?.({
                        select: true,
                        elementId,
                        overlayId,
                    });
                }
            );
        }
    };

    const clearHighlight = (elementId: string) => {
        if (elementId in overlaysListeningToState.value) {
            Object.entries(overlaysListeningToState.value[elementId]).forEach(
                ([overlayId, definition]) => {
                    definition.onStateChange?.({
                        select: false,
                        elementId,
                        overlayId,
                    });
                }
            );
        }
    };

    onMounted(() => {
        isMounted.value = true;
    });

    onUnmounted(() => {
        isMounted.value = false;
    });

    return {
        attach,
        getHtml,
        setLayerVisibility,
        highlight,
        clearHighlight,
    };
}
